import { useEffect, useRef, useState } from 'react';

/**
 * Run an async fetcher and expose { data, loading, error, refetch }.
 *
 * fetcher: () => Promise<T>  — must be referentially stable across renders,
 *                              or dependencies must list whatever it closes over.
 * deps: dependency list.
 */
export function useApi(fetcher, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cancelled = useRef(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    cancelled.current = false;
    setLoading(true);
    setError(null);
    Promise.resolve()
      .then(() => fetcher())
      .then((d) => {
        if (!cancelled.current) setData(d);
      })
      .catch((e) => {
        if (!cancelled.current) setError(e);
      })
      .finally(() => {
        if (!cancelled.current) setLoading(false);
      });
    return () => { cancelled.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const refetch = () => setTick((t) => t + 1);
  return { data, loading, error, refetch };
}

/** Subscribe to viewport-intersect — used by reveal-on-scroll + count-up. */
export function useInView(options = { threshold: 0.15, once: true }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        if (options.once) io.unobserve(node);
      } else if (!options.once) {
        setInView(false);
      }
    }, { threshold: options.threshold });
    io.observe(node);
    return () => io.disconnect();
  }, [options.threshold, options.once]);
  return [ref, inView];
}
